/**
 * 이상한 방 - 타이틀 화면 (순수 DOM)
 *
 * ## 이 화면의 존재 이유는 오디오다
 *
 * 브라우저는 **사용자 제스처 없이 AudioContext를 시작해 주지 않는다.** 그래서 이 앱의 소리는
 * 누군가 무언가를 누르기 전까지 통째로 침묵이고, 지금까지 그 해제는 단계 시작 버튼에 얹혀
 * 있었다(`main.ts`의 `startLevel`). 그 말은 **첫 화면에서는 배경음이 절대 나올 수 없다**는 뜻이다.
 * 단계 선택은 앱을 켜고 가장 오래 머무는 화면인데 거기가 항상 무음이었다.
 *
 * 그래서 문을 하나 세운다. 이름과 버튼 하나뿐인 화면이고, 그 버튼이 **첫 제스처를 보장한다.**
 * 이 화면이 하는 일은 그것 하나이므로, 여기에 무엇을 더 얹을지 고민할 때의 기준도 그것 하나다.
 *
 * ## 그래서 `onStart`는 제스처 콜스택 안에서 불린다
 *
 * 이 파일에서 가장 틀리기 쉬운 곳이며, 틀려도 **화면은 멀쩡해 보인다.** 소리만 안 난다.
 *
 * `setTimeout`·`requestAnimationFrame`·트랜지션 종료 이벤트로 `onStart`를 미루면 브라우저가
 * 그 호출을 사용자 제스처로 인정하지 않고, `audio.unlock()`이 정지 상태의 컨텍스트를 돌려준다.
 * 퇴장 연출을 넣고 싶으면 **`onStart`를 먼저 부르고 연출은 그 뒤에 돌려라.** 순서가 규약이다.
 *
 * 지금은 퇴장 연출이 없다. 클릭 리스너가 하는 일은 `onStart()` 한 줄뿐이고, 화면을 접는 것은
 * 배선의 몫이다(`hide()`). 연출을 이 안에 넣으면 배선의 `hide()`와 경합하는데,
 * 0.5초 보고 지나가는 문에서 그 복잡도를 살 이유가 없다.
 *
 * ## 매번 보여 준다
 *
 * "한 번 본 사람은 건너뛰기"를 넣지 마라. 오디오 해제는 **새로고침마다 다시 필요하므로**
 * 그 기능이 있으면 두 번째 방문부터 이 화면의 목적이 사라진다. 대신 빠르게 지나갈 수 있어야
 * 한다 - 뜨는 순간 시작 버튼에 포커스가 있고(Enter 한 번이면 끝난다), 등장 연출 도중에도 눌린다.
 *
 * ## 상태를 갖지 않는다
 *
 * 다음에 무엇을 띄울지도, 소리를 어떻게 여는지도 전부 바깥의 몫이다. 이 화면이 아는 것은
 * "사람이 방금 버튼을 눌렀다"뿐이다.
 */

import { onLocaleChange, t } from '../i18n'

import { createElement } from './dom'

// 색·그림자·글꼴 토큰의 원본. 이 화면은 앱의 **첫 화면**이라 남이 먼저 로드해 주기를 기대할 수
// 없다(선택·안내 화면은 `gameHud.ts`를 거쳐 이 토큰을 이미 받고 있다).
import './gameHud.css'
import './titleScreen.css'

/* --- 타입 --------------------------------------------------------------------- */

export type TitleScreenOptions = Readonly<{
  mount: HTMLElement
  /**
   * 시작 버튼이 눌렸다. **이 콜백은 사용자 제스처 콜스택 안에서 동기로 불린다.**
   *
   * 그러므로 오디오 해제(`audio.unlock()` · `bgm.unlock()`)를 여기서 바로 불러라.
   * 여기서 `await`나 타이머 뒤로 미루면 그 시점은 더 이상 제스처가 아니다.
   */
  onStart: () => void
}>

export type TitleScreenHandle = Readonly<{
  show: () => void
  hide: () => void
  dispose: () => void
}>

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 타이틀 화면을 만들어 `mount`에 붙인다. **처음에는 숨겨져 있다**(다른 오버레이와 같은 규약).
 *
 * 배선은 `show()`를 불러야 한다. 부르지 않으면 화면도 안 뜨고 오디오도 안 열린다.
 */
export function createTitleScreen(options: TitleScreenOptions): TitleScreenHandle {
  const root = createElement('div', 'ts-root')
  root.hidden = true
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-modal', 'true')
  root.setAttribute('aria-labelledby', 'ts-name')

  /*
   * 테이프와 이름은 겹쳐 놓는다(`titleScreen.css`의 같은 절). 그래서 둘을 한 칸에 묶는
   * 상자가 하나 필요하고, 이 상자가 하는 일은 그 한 가지뿐이다.
   */
  const band = createElement('div', 'ts-band')

  // 글자가 없는 장식이다. 낭독기에게는 없는 것과 같다.
  const tape = createElement('div', 'ts-tape')
  tape.setAttribute('aria-hidden', 'true')

  const name = createElement('h1', 'ts-name')
  name.id = 'ts-name'
  band.append(tape, name)

  /*
   * 진짜 `<button>`이다. 키보드로 닿고 Enter·Space로 눌리며 낭독기가 버튼이라고 말한다.
   * `div`에 클릭을 걸면 그 셋을 전부 손으로 다시 만들어야 하고, 대개 하나씩 빠진다.
   */
  const startButton = createElement('button', 'ts-start')
  startButton.type = 'button'

  root.append(band, startButton)
  options.mount.append(root)

  /**
   * 지금 이 화면이 시작을 받을 수 있는가.
   *
   * `show()`가 켜고 첫 시작에서 꺼진다. 한 번 뜬 문을 두 번 여는 일을 막는 것인데,
   * 폰의 더블탭이나 "Enter를 연타하는 사람"이 실제로 그 경로다. 두 번 불리면 배선 쪽에서
   * 배경음이 두 번 시작되어 곡이 겹친다.
   */
  let armed = false

  /** 이름과 버튼 문구. 언어가 바뀌면 이것만 다시 그리면 된다. */
  function render(): void {
    // 게임 이름은 이미 사전에 있다. 타이틀 전용 키를 새로 만들면 두 이름이 갈라진다.
    name.textContent = t('common.appName')
    startButton.textContent = t('title.start')
  }

  render()

  /*
   * 언어 구독을 이 화면이 직접 쥔다. 선택·안내 화면은 만들어질 때 문구를 굽기 때문에 배선이
   * 통째로 다시 만들어 주지만(`main.ts`의 `rebuildOverlays`), 여기 있는 문구는 둘뿐이라
   * 다시 그리는 것으로 충분하다. 문장을 조립하는 곳도, 굳는 값도 없다.
   */
  const stopLocaleWatch = onLocaleChange(render)

  /**
   * 시작. **이 함수는 언제나 제스처 이벤트의 콜스택 안에서 실행된다**(위 머리말).
   * 여기에 비동기를 들이지 마라.
   */
  function start(): void {
    if (!armed) return
    armed = false
    options.onStart()
  }

  /**
   * Enter·Space로도 시작한다. 버튼에 포커스가 있으면 브라우저가 이미 클릭을 만들어 주므로
   * 그때는 비켜선다(비켜서지 않으면 한 번 누른 키가 두 번 시작한다).
   *
   * 창 단위로 듣는 이유는 사람이 바탕을 한 번 클릭하면 포커스가 `body`로 가기 때문이다.
   * 그 상태에서 Enter가 죽으면 "키보드로는 못 넘어가는 화면"이 된다.
   * keydown도 제스처이므로 이 경로로 들어와도 오디오는 열린다.
   */
  function handleKeyDown(event: KeyboardEvent): void {
    if (root.hidden) return
    if (event.altKey || event.ctrlKey || event.metaKey) return

    /*
     * Tab을 여기서 가둔다. **이 화면이 `aria-modal`이라고 말하고 있기 때문이다.**
     *
     * 바탕이 불투명해서 뒤가 안 보일 뿐, 뒤에 있는 버튼들은 여전히 탭 순서에 남아 있다
     * (톱니 하나와 모바일 조작 넷, 실측 다섯 개). 그래서 Tab 한 번이면 포커스가 **보이지
     * 않는 곳으로 빠져나간다** - 초점 테두리는 불투명한 바탕 뒤에 그려지므로 키보드로 오는
     * 사람 눈에는 포커스가 통째로 사라진 것으로 보인다. 톱니가 그렇게 눌리면 설정 패널이
     * 이 화면 뒤(z-index 47 < 50)에서 열려서, 보이지도 않는 것이 켜진 채로 남는다.
     *
     * 여기 있는 초점은 하나뿐이라 가두는 일이 곧 제자리로 돌리는 일이다. 대화상자가 원래
     * 하는 그대로다. Shift+Tab도 같은 자리로 돌아온다 - 갈 곳이 하나뿐이라 방향이 없다.
     */
    if (event.key === 'Tab') {
      event.preventDefault()
      startButton.focus()
      return
    }

    if (event.key !== 'Enter' && event.key !== ' ') return
    if (document.activeElement === startButton) return

    // Space는 기본 동작이 스크롤이다. 막지 않으면 화면이 한 번 튄다.
    event.preventDefault()
    start()
  }

  startButton.addEventListener('click', start)
  window.addEventListener('keydown', handleKeyDown)

  return {
    show(): void {
      armed = true
      root.hidden = false
      /*
       * 포커스를 버튼에 올려 둔다. 이 화면을 아는 사람은 Enter 한 번으로 지나가야 하고,
       * `aria-modal` 대화상자를 열어 놓고 포커스를 바깥에 두면 낭독기가 뒤의 화면을 읽는다.
       */
      startButton.focus()
    },

    hide(): void {
      armed = false
      // 숨긴 요소 안에 포커스를 남기면 그 뒤의 Tab이 어디서 시작되는지 브라우저마다 갈린다.
      if (root.contains(document.activeElement)) startButton.blur()
      root.hidden = true
    },

    dispose(): void {
      stopLocaleWatch()
      startButton.removeEventListener('click', start)
      window.removeEventListener('keydown', handleKeyDown)
      root.remove()
    },
  }
}
